import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography, Card, Row, Col, Button, Space, Spin, Empty,
  Modal, Form, Input, message, Result,
} from 'antd';
import {
  CalendarOutlined, EnvironmentOutlined, TeamOutlined,
} from '@ant-design/icons';
import { scenarioSiteAPI } from '../../services/api';
import { getContentWrapperStyle } from './templateStyles';

const { Title, Text, Paragraph } = Typography;

export default function ScenarioSiteEvents({ slug, siteInfo }) {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regModal, setRegModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  const [form] = Form.useForm();

  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';

  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 12;
  const btnRadius = isMinimal ? 0 : isClassic ? 4 : undefined;
  const contentFont = getContentWrapperStyle(template, brandColor);

  useEffect(() => {
    (async () => {
      try {
        const res = await scenarioSiteAPI.getEvents(slug, i18n.language);
        setEvents(res.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [slug, i18n.language]);

  const handleRegister = async (values) => {
    if (!regModal) return;
    setSubmitting(true);
    try {
      await scenarioSiteAPI.registerEvent(slug, regModal.id, values);
      setRegSuccess(true);
    } catch (e) {
      message.error(e.message || t('publicSite.events.registerFailed'));
    }
    setSubmitting(false);
  };

  const closeModal = () => {
    setRegModal(null);
    setRegSuccess(false);
    form.resetFields();
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px', ...contentFont }}>
      <Title level={2} style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 8,
        fontWeight: isMinimal ? 300 : 600,
        letterSpacing: isMinimal ? 1 : 0,
        borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
        paddingBottom: isMinimal ? 8 : 0,
        display: isMinimal ? 'inline-block' : 'block',
      }}>
        {t('publicSite.events.title')}
      </Title>
      <Paragraph type="secondary" style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 40,
      }}>
        {t('publicSite.events.subtitle')}
      </Paragraph>

      {events.length === 0 ? (
        <Empty description={t('publicSite.events.noEvents')} />
      ) : (
        <Row gutter={[16, 16]}>
          {events.map(evt => (
            <Col key={evt.id} xs={24} md={12}>
              <Card
                hoverable
                onClick={() => setRegModal(evt)}
                cover={evt.cover_image_url ? (
                  <div style={{ overflow: 'hidden' }}>
                    <img alt={evt.title} src={evt.cover_image_url} style={{ width: '100%', display: 'block' }} />
                  </div>
                ) : null}
                style={{
                  borderRadius: cardRadius,
                  overflow: 'hidden',
                  borderTop: (!evt.cover_image_url && !isMinimal) ? `3px solid ${brandColor}` : undefined,
                  border: isMinimal ? '1px solid #e8e8e8' : undefined,
                  borderTopColor: isMinimal ? '#e8e8e8' : undefined,
                  boxShadow: isMinimal ? 'none' : isClassic ? '0 1px 4px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>{evt.title}</Title>
                <Space direction="vertical" size={6} style={{ width: '100%', marginBottom: 16 }}>
                  {evt.event_date && (
                    <Space>
                      <CalendarOutlined style={{ color: brandColor }} />
                      <Text>{new Date(evt.event_date).toLocaleString('zh-TW', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                      })}</Text>
                    </Space>
                  )}
                  {evt.location && (
                    <Space>
                      <EnvironmentOutlined style={{ color: brandColor }} />
                      <Text>{evt.location}</Text>
                    </Space>
                  )}
                  {evt.spots_left != null && (
                    <Space>
                      <TeamOutlined style={{ color: brandColor }} />
                      <Text>{t('publicSite.events.spotsLeft')}<Text strong style={{ color: evt.spots_left < 5 ? '#ff4d4f' : brandColor }}>{evt.spots_left}</Text></Text>
                    </Space>
                  )}
                </Space>
                {evt.description && (
                  <Paragraph ellipsis={{ rows: 2 }} style={{ color: '#666', marginBottom: 12 }}>
                    {evt.description}
                  </Paragraph>
                )}
                <Button type="primary" style={{
                  background: brandColor,
                  borderColor: brandColor,
                  borderRadius: btnRadius,
                }}>
                  {t('publicSite.events.register')}
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={regModal ? t('publicSite.events.registerTitle', { title: regModal.title }) : ''}
        open={!!regModal}
        onCancel={closeModal}
        footer={null}
        width={500}
      >
        {regSuccess ? (
          <Result
            status="success"
            title={t('publicSite.events.registerSuccess')}
            subTitle={t('publicSite.events.registerSuccessMsg')}
            extra={<Button onClick={closeModal}>{t('common.close')}</Button>}
          />
        ) : (
          <Form form={form} layout="vertical" onFinish={handleRegister}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contact_name" label={t('publicSite.form.name')} rules={[{ required: true, message: t('publicSite.form.nameRequired') }]}>
                  <Input placeholder={t('publicSite.form.namePlaceholder')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="company_name" label={t('publicSite.form.company')}>
                  <Input placeholder={t('publicSite.form.companyPlaceholder')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="email" label={t('publicSite.form.email')} rules={[
              { required: true, message: t('publicSite.form.emailRequired') },
              { type: 'email', message: t('publicSite.form.emailInvalid') },
            ]}>
              <Input placeholder={t('publicSite.form.emailPlaceholder')} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="phone" label={t('publicSite.form.phone')}>
                  <Input placeholder={t('publicSite.form.phonePlaceholder')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="job_title" label={t('publicSite.form.jobTitle')}>
                  <Input placeholder={t('publicSite.form.jobTitlePlaceholder')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="note" label={t('publicSite.form.notes')}>
              <Input.TextArea rows={2} placeholder={t('publicSite.form.notesPlaceholder')} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary" htmlType="submit" loading={submitting}
                block size="large"
                style={{
                  background: brandColor,
                  borderColor: brandColor,
                  borderRadius: btnRadius,
                }}
              >
                {t('publicSite.events.submitRegistration')}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
